import React from 'react';
import { useTranslation } from 'react-i18next';
// Its own one-key namespace, never `../../i18n`: this is in the initial bundle.
import { NS } from './i18n';
import './InboxChatsSkeleton.css';

/** Chat cards drawn in the list — enough to fill a laptop-height list. */
const CARD_ROWS = [0, 1, 2, 3, 4];

const Bone: React.FC<{ className: string }> = ({ className }) => (
  <span className={`inbox-chats-skeleton__bone ${className}`} />
);

/** One conversation card, as the list's own loading card draws it (`InboxChatPreviews`). */
const CardSkeleton: React.FC = () => (
  <div className="inbox-chats-skeleton__card">
    <div className="inbox-chats-skeleton__card-head">
      <Bone className="inbox-chats-skeleton__avatar" />
      <Bone className="inbox-chats-skeleton__line inbox-chats-skeleton__line--name" />
    </div>
    <Bone className="inbox-chats-skeleton__line inbox-chats-skeleton__line--title" />
    <Bone className="inbox-chats-skeleton__line inbox-chats-skeleton__line--preview" />
  </div>
);

export interface InboxChatsSkeletonProps {
  /**
   * Adds the agent screens' 24px gutter (`AgentShell` in `AssistantDetailLayout`),
   * for `RouteSkeleton`, which draws it straight inside the app chrome.
   */
  standalone?: boolean;
}

/**
 * Loading shape of Inbox → Chats (SL-1908, part 4). One shape from a cold load
 * to data: `RouteSkeleton` (inside the app chrome), the agent layout while the
 * agents load and the route's Suspense fallback all draw this; the page then
 * mounts with the same header and panes, its list showing the same cards while
 * the first page of conversations loads.
 *
 * The page as it arrives: breadcrumb, the toolbar (tabs, search, the Channel
 * filter), the list of chat cards beside the empty conversation pane. No chat is
 * selected on arrival, so the details column is not there yet. On a phone only
 * the list shows, as on the page.
 *
 * Its own classes, never the page's: the page's sheets come with its chunk and
 * this paints before it. Nothing visible but bones; the status carries its
 * translated text inside, visually hidden, so a screen reader announces it (a
 * label alone on an empty live region is often never read). No `aria-busy`:
 * on a live region it tells assistive tech to hold the announcement.
 */
export const InboxChatsSkeleton: React.FC<InboxChatsSkeletonProps> = ({ standalone = false }) => {
  const { t } = useTranslation(NS);

  return (
    <div
      className={`inbox-chats-skeleton${standalone ? ' inbox-chats-skeleton--standalone' : ''}`}
      role="status"
      data-testid="inbox-chats-skeleton"
    >
      <span className="inbox-chats-skeleton__sr-only">{t('loading')}</span>
      <div className="inbox-chats-skeleton__header" aria-hidden="true">
        <Bone className="inbox-chats-skeleton__crumb" />
        <div className="inbox-chats-skeleton__toolbar">
          <Bone className="inbox-chats-skeleton__tabs" />
          <Bone className="inbox-chats-skeleton__search" />
          <div className="inbox-chats-skeleton__filters">
            <Bone className="inbox-chats-skeleton__filters-label" />
            <Bone className="inbox-chats-skeleton__select" />
          </div>
        </div>
      </div>
      <div className="inbox-chats-skeleton__layout" aria-hidden="true">
        <div className="inbox-chats-skeleton__list">
          <div className="inbox-chats-skeleton__list-head">
            <Bone className="inbox-chats-skeleton__line inbox-chats-skeleton__line--count" />
            <Bone className="inbox-chats-skeleton__sort" />
          </div>
          {CARD_ROWS.map((row) => (
            <CardSkeleton key={row} />
          ))}
        </div>
        <div className="inbox-chats-skeleton__conversation" />
      </div>
    </div>
  );
};
